import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ProjectTemplate } from '../modules/projects/projects.types';
import { RelatedToType } from '@shared/enums';

export interface LayoutsPagination<T> {
  count: number;
  next: string;
  offset: number;
  previous: string | null;
  templates: ProjectTemplate<T>[];
}
export type LayoutsGetResponse<T> = LayoutsPagination<T> | ProjectTemplate<T>[];

@Injectable({ providedIn: 'root' })
export class LayoutsService {
  constructor(private readonly httpClient: HttpClient) {}

  get<T>(
    relatedTo?: RelatedToType | string,
    page?: number,
    pageSize?: number,
    query?: string,
  ): Observable<LayoutsGetResponse<T>> {
    const queryParams = new URLSearchParams({
      ...(relatedTo ? { related_to_type: relatedTo } : {}),
      ...(query ? { q: query } : {}),
      ...(page ? { page: page.toString() } : {}),
      ...(pageSize ? { per_page: pageSize.toString() } : {}),
    });

    const url = `project_templates?${queryParams.toString()}`;

    return this.httpClient
      .get<LayoutsPagination<T> | { response: LayoutsPagination<T> } | { templates: ProjectTemplate<T>[] }>(url)
      .pipe(
        map((result) => {
          const payload = 'response' in result ? result.response : result;

          if (pageSize) {
            return payload as LayoutsPagination<T>;
          }

          if ('templates' in payload) {
            return payload.templates;
          }

          return payload as ProjectTemplate<T>[];
        }),
      );
  }

  extractTemplates<T>(response?: LayoutsGetResponse<T> | null): ProjectTemplate<T>[] {
    if (!response) {
      return [];
    }

    return Array.isArray(response) ? response : (response.templates ?? []);
  }

  getById(id: number): Observable<ProjectTemplate<any>> {
    return this.httpClient.get<any>(`project_templates/${id}`).pipe(map(({ project_template }) => project_template));
  }

  create<T>(projectTemplate: ProjectTemplate<T>): Observable<ProjectTemplate<T>> {
    return this.httpClient
      .post<{ project_template: ProjectTemplate<T> }>(`project_templates`, {
        project_template: projectTemplate,
      })
      .pipe(map(({ project_template }) => project_template));
  }

  update<T>(projectTemplate: ProjectTemplate<T>): Observable<ProjectTemplate<T>> {
    return this.httpClient
      .put<{ project_template: ProjectTemplate<T> }>(`project_templates/${projectTemplate.id}`, {
        project_template: projectTemplate,
      })
      .pipe(map(({ project_template }) => project_template));
  }

  getVersions<T>(id: number): Observable<ProjectTemplate<T>[]> {
    return this.httpClient
      .get<{ versions: ProjectTemplate<T>[] }>(`project_templates/${id}/versions`)
      .pipe(map(({ versions }) => versions.sort((a, b) => b.id - a.id)));
  }

  restoreVersion<T>(id: number, versionId: number): Observable<ProjectTemplate<T>> {
    return this.httpClient
      .post<{ version: ProjectTemplate<T> }>(`project_templates/${id}/restore_version`, { version_id: versionId })
      .pipe(map(({ version }) => version));
  }

  delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`project_templates/${id}`).pipe(map(() => void 0));
  }
}
